// Sidebar.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import '../App.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';
import { SidebarData, MobileSubMenuData } from './SidebarData';

function Sidebar() {
  const { t } = useTranslation('sidebar');
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authPromptItem, setAuthPromptItem] = useState(null);
  const sidebarRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get('/api/denglu', {
          withCredentials: true,
        });
        if (!cancelled) setIsLoggedIn(Boolean(response.data?.loggedIn));
      } catch {
        if (!cancelled) setIsLoggedIn(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const requireAuthOrPrompt = useCallback(
    (item, e) => {
      if (!item.requiresAuth || isLoggedIn) return false;
      if (e) e.preventDefault();
      setAuthPromptItem(item);
      return true;
    },
    [isLoggedIn]
  );

  const handleAuthPromptClose = useCallback(() => {
    setAuthPromptItem(null);
  }, []);

  const handleAuthPromptLogin = useCallback(() => {
    const redirectTarget = authPromptItem?.link || '/';
    setAuthPromptItem(null);
    navigate(`/login?redirect=${encodeURIComponent(redirectTarget)}`);
  }, [authPromptItem, navigate]);

  const toggleMenu = useCallback((titleKey) => {
    setExpandedMenus((prevState) => ({
      ...prevState,
      [titleKey]: !prevState[titleKey],
    }));
  }, []);

  // 確保滾動時底部有足夠空間
  useEffect(() => {
    const handleScroll = () => {
      if (sidebarRef.current) {
        const sidebarHeight = sidebarRef.current.scrollHeight;
        const viewportHeight = sidebarRef.current.clientHeight;
        const scrollPosition = sidebarRef.current.scrollTop;

        if (sidebarHeight - scrollPosition - viewportHeight < 200) {
          sidebarRef.current.style.paddingBottom = '200px';
        }
      }
    };

    const sidebarElement = sidebarRef.current;
    if (sidebarElement) {
      sidebarElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (sidebarElement) {
        sidebarElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  // 監聽窗口大小變化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    if (savedDarkMode) {
      document.body.classList.add('dark-mode');
    }

    const handleCollapse = () => {
      setExpandedMenus([]);
      localStorage.setItem('expandedMenus', JSON.stringify([]));
    };

    window.addEventListener('collapseSidebar', handleCollapse);

    return () => {
      window.removeEventListener('collapseSidebar', handleCollapse);
    };
  }, []);

  // 手機模式：點擊 sidebar 外面時收起
  useEffect(() => {
    if (!isMobile) return;

    const handleOutsideClick = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('touchstart', handleOutsideClick);
    return () => document.removeEventListener('touchstart', handleOutsideClick);
  }, [isMobile]);

  // 處理菜單項點擊
  const handleMenuClick = useCallback(
    (item, e) => {
      if (requireAuthOrPrompt(item, e)) return;

      setActiveMenuItem(item.titleKey);

      if (isMobile) {
        setIsExpanded(false);
        setIsClicked(true);
        navigate(item.link);

        setTimeout(() => {
          setIsClicked(false);
        }, 500);
      } else {
        navigate(item.link);
      }
    },
    [isMobile, navigate, requireAuthOrPrompt]
  );

  // 手機：第一次 tap 展開 sidebar，已展開則讓事件正常走到 handleMenuClick
  const handleSidebarClick = useCallback(
    (e) => {
      if (isMobile && !isExpanded) {
        e.stopPropagation();
        setIsExpanded(true);
      }
    },
    [isMobile, isExpanded]
  );

  // 桌機 hover 事件
  const handleSidebarMouseEnter = useCallback(() => {
    if (!isMobile) {
      setIsExpanded(true);
    }
  }, [isMobile]);

  const handleSidebarMouseLeave = useCallback(() => {
    if (!isMobile) {
      setIsExpanded(false);
    }
  }, [isMobile]);

  const renderMenuItem = (item, key, level = 0) => {
    const isActive = location.pathname === item.link;
    const hasActiveChild =
      item.subMenu &&
      item.subMenu.some((subItem) => location.pathname === subItem.link);
    const isMenuItemActive = activeMenuItem === item.titleKey;

    if (item.subMenu) {
      return (
        <li
          key={key}
          className={`menu-item has-submenu level-${level}`}
          data-menu-key={item.titleKey}
        >
          <div className="menu-item-content">
            <div
              className={`menu-link ${isActive || isMenuItemActive ? 'active-link' : ''}`}
              onClick={(e) => handleMenuClick(item, e)}
            >
              {item.icon && <span className="menu-icon">{item.icon}</span>}
              <span
                className="menu-text"
                style={{
                  display: isMobile && !isExpanded ? 'none' : 'inline-block',
                }}
              >
                {t(item.titleKey)}
              </span>
            </div>
            <button
              className="submenu-toggle"
              onClick={() => toggleMenu(item.titleKey)}
              aria-expanded={expandedMenus[item.titleKey]}
            >
              {expandedMenus[item.titleKey] ? <FaAngleUp /> : <FaAngleDown />}
            </button>
          </div>

          {(expandedMenus[item.titleKey] || hasActiveChild) && (
            <ul className={`sub-menu level-${level}`}>
              {item.subMenu.map((subItem, subKey) =>
                renderMenuItem(subItem, `${key}-${subKey}`, level + 1)
              )}
            </ul>
          )}
        </li>
      );
    } else {
      return (
        <li key={key} className={`menu-item level-${level}`}>
          <Link
            to={item.link}
            className={`menu-link ${isActive || isMenuItemActive ? 'active-link' : ''}`}
            onClick={(e) => handleMenuClick(item, e)}
          >
            {item.icon && <span className="menu-icon">{item.icon}</span>}
            <span
              className="menu-text"
              style={{
                display: isMobile && !isExpanded ? 'none' : 'inline-block',
              }}
            >
              {t(item.titleKey)}
            </span>
          </Link>
        </li>
      );
    }
  };

  return (
    <>
      <div
        className={`Sidebar ${isExpanded ? 'expanded' : ''} ${isMobile && isClicked ? 'clicked' : ''}`}
        ref={sidebarRef}
        onClick={handleSidebarClick}
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
      >
        <ul className="SidebarList">
          {SidebarData.map((item, key) => {
            return renderMenuItem(item, key);
          })}

          {isMobile &&
            MobileSubMenuData.map((item, key) => {
              return renderMenuItem(item, `mobile-${key}`, 0);
            })}
        </ul>
      </div>

      {authPromptItem && (
        <div
          className="auth-prompt-overlay"
          role="presentation"
          onClick={handleAuthPromptClose}
        >
          <div
            className="auth-prompt-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="auth-prompt-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="auth-prompt-title">
              {t('sidebar.loginPromptTitle', 'Login required')}
            </h2>
            <p>{t('sidebar.loginPromptBody', 'Please login to continue.')}</p>
            <div className="auth-prompt-actions">
              <button
                type="button"
                className="auth-prompt-secondary"
                onClick={handleAuthPromptClose}
              >
                {t('sidebar.loginPromptCancel', 'Cancel')}
              </button>
              <button
                type="button"
                className="auth-prompt-primary"
                onClick={handleAuthPromptLogin}
              >
                {t('sidebar.loginPromptAction', 'Login')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sidebar;
